import React from 'react';
import clsx from 'clsx';

import Button from '@common/Button';
import Container from '@common/Container';
import Section from '@common/Section';
import Img from '@common/Image';
import Row from '@common/Row';
import RichText from '@common/RichText';
import ImageTextOverlay from '@common/ImageTextOverlay';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y } from 'swiper/modules';
import SmallTitle from '@common/SmallTitle';

import Video from '@common/blocks/flex/Video';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

import * as styles from '@common/blocks/flex/flex.module.scss';

const HomepageImageBothSides = ({ data }) => {
  const {
    title,
    titleAlignment,
    showTitle,
    text,
    textAlignment,
    listClass,
    showImages,
    imagePosition,
    imagePositionTop,
    imagePositionTopLg,
    imageWidth,
    imageMaxHeight,
    imageNegativeMargin,
    buttons,
    images,
    links,
    video,
    topMargin,
    bottomMargin,
    bgColour,
    bgNoPadding,
    bgFullwidth
  } = data;
  return (
    <Section style={{ marginTop: `calc(${imageNegativeMargin} + 50px)` }} spacing={clsx(topMargin, bottomMargin ? bottomMargin : 'mb-7')} className={clsx({
        [`bg-${bgColour}`]: bgColour,
        'py-5': bgColour && !bgNoPadding,
        'container card': !bgFullwidth,
        [styles.removeTopMargin]: imageNegativeMargin && !imagePositionTop
      })}>
      <Container>
        {video &&
          <Video data={data}/>
        }
        <Row className={clsx(`d-flex flex-column align-items-center justify-content-center flex-md-row`, {
            'flex-md-row-reverse': imagePosition === 'Left' && !imagePositionTopLg,
            'flex-column-reverse': imagePositionTop || imagePositionTopLg,
            'justify-content-center': (!images || !links) && titleAlignment === 'Center'
          })}>
          {(images && images[0]) &&
            <div className='col-3 d-none d-lg-block'>
              <Img
                key={images[0].id}
                loading='eager'
                image={images[0].gatsbyImageData}
                file={images[0].file}
                alt={images[0].description}
                style={{ maxHeight: imageMaxHeight }}
                imgStyle={{ objectFit: imageMaxHeight ? 'contain' : '', width: '100%'}}
              />
            </div>
          }
          {(text || buttons) &&
            <div className={clsx('col-auto pt-5', {
                'text-center': textAlignment === 'Center',
                'text-end': textAlignment === 'Right' || !images && titleAlignment === 'Right',
                'text-start': textAlignment === 'Left',
              })}>
              {(showTitle && title) &&
                <SmallTitle>{title}</SmallTitle>
              }
              {text &&
                <RichText
                  json={text}
                  h1='mb-3'
                  h2='mb-3'
                  ul={listClass}
                  p='mb-3'
                  a='fs-16'
                />
              }
              {buttons && 
                <div className={clsx('d-flex flex-column flex-sm-row justify-content-center', {
                  'mb-3': imagePosition === 'Center',
                  'justify-content-lg-start ms-lg-n1': (images || links) && imagePosition !== 'Center',
                  })}>
                  {buttons.map(button => (
                    <Button
                      key={button.id}
                      to={button.link}
                      variant={button.variant}
                      size={button.size}
                      addClass={clsx('scale-1 mb-2 m-sm-1', button.class)}
                      copy={button.copy}
                      icon={button.icon}
                      iconSize={button.iconSize}
                      iconPosition={button.iconPosition}
                      modal={button.modal}
                    />
                  ))}
                </div>
              }
            </div>
          }
          {(images && images[1]) &&
            <div className='col-3 d-none d-lg-block'>
              <Img
                key={images[1].id}
                loading='eager'
                image={images[1].gatsbyImageData}
                file={images[1].file}
                alt={images[1].description}
                style={{ maxHeight: imageMaxHeight }}
                imgStyle={{ objectFit: imageMaxHeight ? 'contain' : '', width: '100%'}}
              />
            </div>
          }
          {images?.length > 2 &&
            <div style={{ marginTop: `-${imageNegativeMargin}` }} className={clsx('col justify-content-center', imageWidth, {
                [`d-none ${showImages}`]: showImages,
                'mb-3 mb-md-0': imagePositionTop,
                'mb-lg-5': imagePositionTopLg,
                [styles.removeTopMargin]: imageNegativeMargin && !imagePositionTop
              })}>
              {images.slice(2).map(image => (
                <Img
                  key={image.id}
                  loading='eager'
                  image={image.gatsbyImageData}
                  file={image.file}
                  alt={image.description}
                  style={{ maxHeight: imageMaxHeight }}
                  imgStyle={{ objectFit: imageMaxHeight ? 'contain' : ''}}
                />  
              ))}
            </div>
          }
          {links &&
            <div style={{ marginTop: `-${imageNegativeMargin}` }} className={clsx('d-flex', imageWidth, {
                [`d-none ${showImages}`]: showImages,
                'mb-3 mb-md-0': imagePositionTop,
                'mb-lg-5': imagePositionTopLg,
                [styles.removeTopMargin]: imageNegativeMargin && !imagePositionTop,
                'customer-stories-links': links.length > 1
              })}>
              {links.map((link, index) => (
                <div key={index} className={clsx('col', {
                    [styles.offsetCards]:index % 2 != 0,
                    'me-3': index !== links.length-1
                  })}>
                  <ImageTextOverlay to={link.link} image={link.image} colour={link.bgColour}>
                    <div className='fs-18 fw-medium' style={{marginBottom:'15px'}}>{link.title}</div>
                    <div className='d-flex align-items-center fs-16 fw-normal'>
                      <span style={{marginRight: '5px'}}>{link.linkCopy}</span>
                      <FontAwesomeIcon icon={faChevronRight} size ='sm'/>
                    </div>
                  </ImageTextOverlay>
                  {link.subtitle && 
                    <div className='mt-3 col-lg-9 fw-medium'>
                      {link.subtitle}
                    </div>
                  }
                </div>
              ))}
            </div>
          }
        </Row>
        {links &&
          <Swiper className='mt-3 d-lg-none'
            modules={[A11y]}
            slidesPerView={1.3}
            spaceBetween={20}
            breakpoints={{
              768: {
                slidesPerView: 2,
              }
            }}
            >
            {links.map((link, index) => (
              <SwiperSlide key={index}>
                <ImageTextOverlay to={link.link} image={link.image} colour={link.backgroundColour}>
                  <div className='fs-18 fw-medium' style={{marginBottom:'15px'}}>{link.title}</div>
                  <div className='d-flex align-items-center fs-16 fw-normal'>
                    <span style={{marginRight: '5px'}}>{link.linkCopy}</span>
                    <FontAwesomeIcon icon={faChevronRight} size ='sm'/>
                  </div>
                </ImageTextOverlay>
              </SwiperSlide>
            ))}
          </Swiper>
        }
      </Container>
    </Section>
  )
}

export default HomepageImageBothSides
